export type UserRole = 'USER' | 'VENDOR' | 'ADMIN';

export const USER_ROLE_OPTIONS = ['USER', 'VENDOR', 'ADMIN'] as const;

/** Columns the API can sort by — mirrors USER_SORT_FIELDS on the backend. */
export const USER_SORT_FIELDS = [
  'created_at',
  'last_login_at',
  'name',
  'role',
  'pet_count',
  'booking_count',
] as const;

export type UserListItem = {
  id: string;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  role: UserRole;
  is_active: boolean;
  /** Relative API path to the avatar stream; null when none is set. */
  avatar_url: string | null;
  pet_count: number;
  booking_count: number;
  order_count: number;
  has_vendor_profile: boolean;
  /** Default-address city/state; null when the account has no saved address. */
  city: string | null;
  state: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  created_at: string;
  last_login_at: string | null;
};

export type UserListResponse = {
  items: UserListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type UserPetSummary = {
  id: string;
  name: string;
  species: string;
  breed_name: string | null;
  sex: string;
  neuter_status: string;
  /** Pre-formatted, e.g. "3y 2m" — the API does the arithmetic. */
  age_label: string | null;
  /**
   * Admin-scoped streaming path for the primary photo, or null.
   *
   * NOT the pet's `primary_photo_url` — that is a user-scoped route which
   * rejects an admin token, so it rendered as a broken image in the console.
   */
  photo_thumb_path: string | null;
  microchip_id: string | null;
  weight_kg: number | null;
  is_active: boolean;
  created_at: string;
  /** Vaccinations whose due date has passed — the card's headline warning. */
  vaccination_overdue: number;
  allergy_count: number;
  active_condition_count: number;
  booking_count: number;
};

export type UserAddress = {
  id: string;
  label: string;
  address: string;
  landmark: string | null;
  city: string;
  state: string;
  pincode: string;
  phone: string | null;
  access_notes: string | null;
  is_default: boolean;
};

export type UserActivity = {
  bookings_total: number;
  bookings_cancelled: number;
  orders_total: number;
  reviews_total: number;
  /** Net captured spend across bookings and orders, minus refunds. */
  lifetime_spend_inr: string;
  refunded_inr: string;
  first_activity_at: string | null;
  last_activity_at: string | null;
  recent_bookings: {
    id: string;
    status: string;
    module_type: string;
    vendor_profile_id: string | null;
    vendor_business_name: string | null;
    pet_id: string | null;
    pet_name: string | null;
    pet_species: string | null;
    price_inr: string;
    payment_status: string | null;
    refunded_inr: string | null;
    cancel_reason: string | null;
    cancelled_by: string | null;
    slot_start: string;
    slot_end: string;
    created_at: string;
  }[];
  recent_orders: {
    id: string;
    status: string;
    vertical: string | null;
    total_inr: string;
    /** Product lines across every shipment, not the shipment count. */
    item_count: number;
    shipment_count: number;
    vendor_names: string[];
    city: string | null;
    prescription_required: boolean;
    payment_status: string | null;
    created_at: string;
  }[];
  recent_reviews: {
    id: string;
    rating: number;
    comment: string | null;
    vendor_business_name: string | null;
    is_hidden: boolean;
    reported_at: string | null;
    created_at: string;
  }[];
};

export type UserDetail = UserListItem & {
  email_verified_at: string | null;
  phone_verified_at: string | null;
  updated_at: string;
  vendor_profile_id: string | null;
  emergency_contact: {
    name: string | null;
    relationship: string | null;
    phone: string | null;
  } | null;
  notifications: { sms: boolean; email: boolean; push: boolean; marketing: boolean };
  addresses: UserAddress[];
  pets: UserPetSummary[];
  activity: UserActivity;
};

export type UserListFilters = {
  page?: number;
  page_size?: number;
  role?: UserRole;
  q?: string;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
  /** 'true' = active only, 'false' = blocked only, '' = both. */
  is_active?: string;
  has_vendor_profile?: string;
  city?: string;
  phone_verified?: string;
  /* ---- customer-shaped filters ---- */
  has_pets?: string;
  has_bookings?: string;
  has_orders?: string;
  has_address?: string;
  /** Signed in within the last N days. */
  active_within_days?: number;
  /** No sign-in for at least N days; never-signed-in accounts included. */
  dormant_days?: number;
};
